import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';
import { pruneUndefined } from '../lib/prune';

import type { ClientCreateInput, ClientUpdateInput } from './schemas';

type CreateClientRequest = components['schemas']['CreateClientRequest'];
type UpdateClientRequest = components['schemas']['UpdateClientRequest'];

export const clientsKeys = {
  all: ['clients'] as const,
  list: (includeDeleted: boolean) => ['clients', 'list', { includeDeleted }] as const,
  one: (id: string) => ['clients', 'one', id] as const,
};

export interface ClientRecord {
  id: string;
  name: string;
  description: string | null;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// Phase 8.5-04 UX-02 — flat list helper for picklists (campaign/warehouse
// selectors, trade-link import client picker). Fetches up to 200 rows in
// one go and returns just the items array — callers don't need
// pagination for dropdowns. The list-screen variant lives below.
export const useClients = (options: { includeDeleted?: boolean } = {}) => {
  const { includeDeleted = false } = options;
  return useQuery({
    queryKey: clientsKeys.list(includeDeleted),
    queryFn: async (): Promise<ClientRecord[]> => {
      const filter: { includeDeleted?: 'true' } = {};
      if (includeDeleted) filter.includeDeleted = 'true';
      const result = await api.GET('/api/clients', {
        params: { query: { sort: '-createdAt', page: '1', pageSize: '200' } },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result).items;
    },
  });
};

// Phase 8.5-05 — server-side URL-driven list (ADR-014). Used by
// `routes/clients/index.tsx` so the URL bar carries filter / sort /
// page state.
export type ClientSort = 'createdAt' | 'name';

export interface ClientsListFilter {
  includeDeleted?: boolean | undefined;
}

export interface ClientsListInput {
  filter: ClientsListFilter;
  sort: { field: ClientSort | null; dir: 'asc' | 'desc' };
  page: number;
  pageSize: number;
}

export interface ClientsListResult {
  items: ClientRecord[];
  page: number;
  pageSize: number;
  total: number;
}

const formatClientSort = (sort: ClientsListInput['sort']): string => {
  const field = sort.field ?? 'createdAt';
  return sort.dir === 'desc' ? `-${field}` : field;
};

export const useClientsList = (input: ClientsListInput) =>
  useQuery({
    queryKey: ['clients', 'paginated', input] as const,
    queryFn: async (): Promise<ClientsListResult> => {
      const filter: { includeDeleted?: 'true' } = {};
      if (input.filter.includeDeleted) filter.includeDeleted = 'true';
      const result = await api.GET('/api/clients', {
        params: {
          query: {
            sort: formatClientSort(input.sort),
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as ClientsListResult;
    },
  });

export const useClient = (id: string | undefined) =>
  useQuery({
    queryKey: id ? clientsKeys.one(id) : ['clients', 'one', 'pending'],
    enabled: Boolean(id),
    queryFn: async (): Promise<ClientRecord> => {
      if (!id) throw new Error('client id required');
      const result = await api.GET('/api/clients/{id}', { params: { path: { id } } });
      return unwrap(result).client;
    },
  });

const invalidateClients = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  qc.invalidateQueries({ queryKey: clientsKeys.all });

export const useCreateClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ClientCreateInput): Promise<ClientRecord> => {
      const payload = pruneUndefined(body) as CreateClientRequest;
      const result = await api.POST('/api/clients', { body: payload });
      return unwrap(result).client;
    },
    onSuccess: () => invalidateClients(qc),
  });
};

export const useUpdateClient = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ClientUpdateInput): Promise<ClientRecord> => {
      const payload = pruneUndefined(body) as UpdateClientRequest;
      const result = await api.PATCH('/api/clients/{id}', {
        params: { path: { id } },
        body: payload,
      });
      return unwrap(result).client;
    },
    onSuccess: () => invalidateClients(qc),
  });
};

export const useDeleteClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const result = await api.DELETE('/api/clients/{id}', { params: { path: { id } } });
      unwrap(result);
    },
    onSuccess: () => invalidateClients(qc),
  });
};
